/** Options for the language picker: which languages it offers, how they
 *  are named, searched, grouped and sorted.
 *
 *  Pure functions over the registry (``@/i18n/languages``), so the
 *  picker stays presentational and every rule here is unit-testable.
 *  Tests pass a fixture registry where more than en/sv/fi are
 *  selectable, because MR A's real registry enables only those three. */
import { languages as registryLanguages, resolveCode, type LanguageEntry } from "@/i18n/languages";

export type PickerKind = "ui" | "output";

export interface LanguageOption {
  code: string;
  /** The language's own name: the primary label, sorted on. */
  endonym: string;
  englishName: string;
  /** The name in the reader's UI language; ``englishName`` when the
   *  runtime cannot name it. */
  localizedName: string;
  short: string;
  dir: "ltr" | "rtl";
  /** Extra search terms: the code, registry aliases and keywords. */
  terms: string[];
}

/** The registry entries the picker may offer for ``kind``. */
export function allowedEntries(
  kind: PickerKind,
  list: readonly LanguageEntry[] = registryLanguages,
): LanguageEntry[] {
  return list.filter((l) => (kind === "ui" ? l.ui : l.output));
}

/** ``code``'s name in ``displayLocale`` (``tyska`` for de in Swedish),
 *  or null when the runtime has no display names for it. */
export function localizedLanguageName(code: string, displayLocale: string): string | null {
  try {
    const name = new Intl.DisplayNames([displayLocale], { type: "language" }).of(code);
    return name && name !== code ? name : null;
  } catch {
    return null;
  }
}

const COLLATOR = new Intl.Collator("und", { sensitivity: "base" });

/** Every option for ``kind``, sorted by endonym with the root
 *  collation: Latin first, then Greek, Cyrillic, Indic, Thai, Hangul
 *  and Han. The endonym is the primary label, so sorting on anything
 *  else would read as unsorted. */
export function buildOptions(
  kind: PickerKind,
  displayLocale: string,
  list: readonly LanguageEntry[] = registryLanguages,
): LanguageOption[] {
  return allowedEntries(kind, list)
    .map((l) => ({
      code: l.code,
      endonym: l.endonym,
      englishName: l.englishName,
      localizedName: localizedLanguageName(l.code, displayLocale) ?? l.englishName,
      short: l.short,
      dir: l.dir,
      terms: [l.code, ...l.aliases.map((a) => a.replace(/-\*$/, "")), ...l.keywords],
    }))
    .sort((a, b) => COLLATOR.compare(a.endonym, b.endonym));
}

/** Case- and accent-insensitive form for matching: ``Français`` and
 *  ``francais`` compare equal. */
export function normalizeSearch(s: string): string {
  return s.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().trim();
}

/** 0 when a name or term starts with ``query``, 1 when one contains
 *  it, null when nothing does. ``query`` is already normalised. */
export function matchRank(option: LanguageOption, query: string): 0 | 1 | null {
  if (!query) return 0;
  let best: 0 | 1 | null = null;
  const fields = [option.endonym, option.englishName, option.localizedName, ...option.terms];
  for (const field of fields) {
    const value = normalizeSearch(field);
    if (value.startsWith(query)) return 0;
    if (best === null && value.includes(query)) best = 1;
  }
  return best;
}

/** The options matching ``query``: prefix matches first, then inner
 *  matches, each in the options' own (collated) order. */
export function searchOptions(options: readonly LanguageOption[], query: string): LanguageOption[] {
  const q = normalizeSearch(query);
  const prefix: LanguageOption[] = [];
  const inner: LanguageOption[] = [];
  for (const option of options) {
    const rank = matchRank(option, q);
    if (rank === 0) prefix.push(option);
    else if (rank === 1) inner.push(option);
  }
  return [...prefix, ...inner];
}

export interface SuggestInput {
  /** The picker's current value. */
  value: string | null;
  /** ``navigator.languages``, most preferred first. */
  navigatorLanguages: readonly string[];
  /** The UI language (offered first-hand for kind ``output``). */
  uiLanguage?: string | null;
  kind: PickerKind;
  /** The codes the picker offers. */
  allowed: readonly string[];
  list?: readonly LanguageEntry[];
}

/** The Suggested group, deduplicated and limited to ``allowed``: the
 *  current value, the browser's languages (through the registry
 *  aliases), and for the output picker the UI language. */
export function suggestedCodes(input: SuggestInput): string[] {
  const allowed = new Set(input.allowed);
  const out: string[] = [];
  const add = (code: string | null | undefined) => {
    if (code && allowed.has(code) && !out.includes(code)) out.push(code);
  };
  add(input.value);
  for (const tag of input.navigatorLanguages) {
    add(resolveCode(tag, allowed, input.list ?? registryLanguages));
  }
  if (input.kind === "output") add(input.uiLanguage);
  return out;
}

/** ``navigator.languages`` (or ``navigator.language``), never throwing. */
export function browserLanguages(): readonly string[] {
  if (typeof navigator === "undefined") return [];
  if (navigator.languages && navigator.languages.length > 0) return navigator.languages;
  return navigator.language ? [navigator.language] : [];
}
